import deepEqual from "deep-equal"
import { arrayWithContents } from "../../../shared_components/utils"

/*
 * Helper function that constructs the "plot list" for the default dashboards
 *
 * specialPlots: an array of plot configs for "preset" plots.  If these contain
 *               an observations array, this will be used to verify that the
 *               all non-optional tags required for the plot are available
 * queryTags: an array of tag data as returned from /warehouse/querytags
 * observationTypeSets: available tags, in the internal "observationTypes" format,
 *                      i.e. objects where the keys are types and the values are
 *                      {tag, label, units}
 *
 * Returns an array of plot-configs, potentially augmented with the attribute
 * `notAvailable`
 */

// TODO - UNIQUE tag vs. 'tag_name'
function getDefaultPlotList(queryTags = [], ...observationTypeSets) {
  const defaultPlots = [],
    availableTags = {},
    seenSlots = {},
    queryTagsByTag = {}

  for (const qt of queryTags) {
    queryTagsByTag[qt.name] = qt
  }

  for (const observationTypes of observationTypeSets) {
    for (const obsType of Object.keys(observationTypes || {})) {
      for (const obsTag of observationTypes[obsType]) {
        // Identity for a "slot" in the plot-list, given that a tag may be listed under more than one type
        const slotKey = [obsType, obsTag.tag].join("__")
        if (!seenSlots[slotKey]) {
          const plotConfig = {
            category: obsType,
            title: obsTag.label || obsTag.tag,
            axis1label: obsTag.units || "unknown",
            observations: [
              {
                tag: obsTag.tag,
                label: obsTag.label || obsTag.tag,
                style: "linespoints",
                colour: "#1f77b4",
                units: obsTag.units,
              },
            ],
            keywords: (queryTagsByTag[obsTag.tag] || {}).keywords,
            dcs_logging_info: (queryTagsByTag[obsTag.tag] || {}).dcs_logging_info, // TODO - UNIQUE
          }
          defaultPlots.push(plotConfig)
          seenSlots[slotKey] = true
          availableTags[obsTag.tag] = plotConfig
        }
      }
    }
  }

  if (queryTags) {
    for (const qt of queryTags) {
      let qtTypes = qt.types
      if (!qtTypes || qtTypes.length === 0) {
        // It's possible for a tag to exist without any source_tags, in which case it
        // won't have any types.  Show this anyway, in an "Unknown" category, to avoid
        // confusion.
        qtTypes = ["Unknown"]
      }
      for (const category of qtTypes) {
        const slotKey = [category, qt.name].join("__")
        if (!seenSlots[slotKey]) {
          if (availableTags[qt.name]) {
            // This tag *is* available, but under another type.  Duplicate the existing plot config:
            defaultPlots.push({
              ...availableTags[qt.name],
              category: category,
            })
            seenSlots[slotKey] = true
          } else {
            // Tag not available in any of the active runs, so create a disabled placeholder
            defaultPlots.push({
              category: category,
              title: qt.label || qt.name,
              notAvailable: true,
              observations: [
                {
                  tag: qt.name,
                  label: qt.label || qt.name,
                  units: (qt.units_options || []).length > 0 ? qt.units_options[0] : "Unknown",
                },
              ],
              keywords: qt.keywords,
              dcs_logging_info: qt.dcs_logging_info, // TODO - UNIQUE
            })
            seenSlots[slotKey] = true
          }
        }
      }
    }
  }

  return [availableTags, defaultPlots]
}

export function makePlotList(specialPlots, queryTags, graphs, ...observationTypeSets) {
  const [availableTags, defaultPlots] = getDefaultPlotList(queryTags, ...observationTypeSets)

  return [
    ...specialPlots.map((plot) => {
      let notAvailable = false,
        count = 0
      if (arrayWithContents(plot.observations)) {
        for (const obs of plot.observations) {
          if (!availableTags[obs.tag] && !obs.optional) {
            // Missing some required data -- exclude.
            notAvailable = true
          } else {
            ++count
          }
        }
        notAvailable = notAvailable || count === 0
      }

      return {
        ...plot,
        category: plot.category,
        notAvailable,
      }
    }),
    ...defaultPlots,
  ].map((plot) => disableActivePlots(plot, graphs))
}

const minObs = (obs) => ({
  tag: obs.tag,
  smp_src: obs.smp_src,
})

function disableActivePlots(plot, graphs) {
  let disabled = false
  for (const activePlot of graphs || []) {
    if (!activePlot) continue
    if (plot.title === activePlot.title) {
      if (
        deepEqual(minObs(plot.observations), minObs(activePlot.observations)) ||
        (activePlot.special === "multi" && deepEqual(minObs(plot.observations), minObs(activePlot.tags)))
      ) {
        disabled = true
        break
      }
    }
  }
  return {
    ...plot,
    disabled,
  }
}
